import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparentOnTop?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ transparentOnTop = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigateToFeatures = () => {
    navigate('/features');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const navigateWithScroll = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const navItems = [
    { name: 'Features', action: navigateToFeatures },
    { name: 'Pricing', action: () => navigateWithScroll('/pricing') },
    { name: 'Contact', action: () => navigateWithScroll('/contact') },
    { name: 'About', action: () => navigateWithScroll('/about') },
  ];

  const isActive = (path: string) => {
    return location.pathname === `/${path.toLowerCase()}`;
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} mb={2}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            color: '#8E54E9',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src="/assets/akmobooks.com.svg" 
            alt="AkMo Books Logo" 
            style={{ height: '23px', width: 'auto' }} 
          />
          AkMo Books
        </Typography>
        <IconButton edge="end" onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.name}
            onClick={item.action}
            sx={{
              py: 1.5,
              color: isActive(item.name) ? '#8E54E9' : '#1a1a1a'
            }}
          >
            <ListItemText 
              primary={item.name} 
              primaryTypographyProps={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
                fontFamily: "'Poppins', sans-serif",
              }} 
            />
          </ListItem>
        ))}
        <ListItem sx={{ mt: 2, flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/signup')}
            sx={{
              backgroundColor: '#8E54E9',
              borderRadius: '50px',
              py: 1.2,
              fontWeight: 'bold',
              fontFamily: "'Poppins', sans-serif",
              color: 'white',
              '&:hover': {
                backgroundColor: '#7540cc',
                boxShadow: '0 8px 25px rgba(142, 84, 233, 0.3)',
              },
            }}
          >
            Get Started
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 1000,
        background: transparentOnTop && !scrolled 
          ? 'transparent' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.05)' : 'none',
        py: { xs: 1.5, md: 2 },
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              color: '#8E54E9',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/assets/akmobooks.com.svg" 
              alt="AkMo Books Logo" 
              style={{ height: '28px', width: 'auto' }} 
            />
            AkMo Books
          </Typography>
          
          {isSmScreen ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#1a1a1a' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center"
                sx={{ flex: 1, mx: 4, gap: 2 }}
              >
                {navItems.map((item) => (
                  <Button 
                    key={item.name}
                    sx={{ 
                      mx: 1, 
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: "'Poppins', sans-serif",
                      position: 'relative',
                      color: isActive(item.name) ? '#8E54E9' : '#1a1a1a',
                      textTransform: 'none',
                      '&:hover': {
                        color: '#8E54E9',
                        background: 'transparent'
                      },
                      '&::after': isActive(item.name) ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 2,
                        left: '50%',
                        width: '30%',
                        height: 3,
                        backgroundColor: '#8E54E9',
                        transform: 'translateX(-50%)',
                        borderRadius: '2px',
                      } : {},
                    }}
                    onClick={item.action}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/signup')}
                  sx={{
                    backgroundColor: '#8E54E9',
                    color: 'white',
                    borderRadius: '50px',
                    px: 4,
                    py: 1.2,
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    fontFamily: "'Poppins', sans-serif",
                    boxShadow: '0 4px 14px rgba(142, 84, 233, 0.2)',
                    '&:hover': {
                      backgroundColor: '#7540cc',
                      boxShadow: '0 6px 20px rgba(142, 84, 233, 0.3)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

const Divider = () => (
  <Box
    sx={{
      height: '1px',
      width: '100%',
      backgroundColor: 'rgba(0,0,0,0.1)',
      my: 1,
    }}
  />
);

export default Navbar; 